import React from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { FlowNode } from '../../../types/flowBuilder';
import { VariableIcon, EyeIcon } from '@heroicons/react/24/outline';

interface VariableNodeProps extends NodeProps<FlowNode['data']> {
  nodeType: 'variable_set' | 'variable_get';
}

const VariableNode: React.FC<VariableNodeProps> = ({ data, selected, nodeType }) => {
  const variableName = data.config?.variableName || 'myVariable';
  const isGetter = nodeType === 'variable_get';
  
  const getVariableTypeIcon = (value: any) => {
    if (value === null || value === undefined) return '❓';
    if (typeof value === 'string') return '📝';
    if (typeof value === 'number') return '🔢';
    if (typeof value === 'boolean') return '✅';
    if (Array.isArray(value)) return '📋';
    if (typeof value === 'object') return '📦';
    return '❓';
  };

  const getValuePreview = (value: any) => {
    if (value === null || value === undefined) return 'null';
    if (typeof value === 'string') return `"${value.length > 20 ? value.substring(0, 20) + '...' : value}"`;
    if (typeof value === 'number') return value.toString();
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    if (Array.isArray(value)) return `Array[${value.length}]`;
    if (typeof value === 'object') return `Object{${Object.keys(value).length}}`;
    return String(value);
  };

  return (
    <div className={`px-4 py-3 shadow-lg rounded-lg bg-gradient-to-r ${
      isGetter 
        ? 'from-emerald-600 to-emerald-700' 
        : 'from-emerald-500 to-emerald-600'
    } border-2 transition-all duration-200 ${
      selected ? 'border-emerald-800 shadow-xl scale-105' : 'border-emerald-400'
    }`}>
      <div className="flex items-center space-x-3">
        {/* Icon */}
        <div className="flex-shrink-0">
          <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
            {isGetter ? (
              <EyeIcon className="w-5 h-5 text-white" />
            ) : (
              <VariableIcon className="w-5 h-5 text-white" />
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-white truncate">
            {data.label}
          </div>
          {data.description && (
            <div className="text-xs text-emerald-100 truncate mt-1">
              {data.description}
            </div>
          )}
          
          {/* Variable name display */}
          <div className="flex items-center mt-1 space-x-2">
            <span className="text-xs text-emerald-100 font-mono bg-black bg-opacity-20 px-1 py-0.5 rounded">
              ${variableName}
            </span>
            <span className="text-xs text-emerald-200">
              {isGetter ? 'lekérdezés' : 'beállítás'}
            </span>
          </div>
        </div>

        {/* Status indicator */}
        <div className="flex-shrink-0">
          <div className={`w-3 h-3 rounded-full ${
            data.hasError ? 'bg-red-500' : 'bg-green-400'
          }`} />
        </div>
      </div>

      {/* Error message */}
      {data.hasError && data.errorMessage && (
        <div className="mt-2 text-xs text-red-200 bg-red-500 bg-opacity-20 px-2 py-1 rounded">
          {data.errorMessage}
        </div>
      )}

      {/* Variable value preview */}
      {data.executionData && (
        <div className="mt-2 text-xs text-emerald-100 bg-black bg-opacity-20 px-2 py-1 rounded">
          <div className="flex items-center space-x-2">
            <span>{getVariableTypeIcon(data.executionData.value)}</span>
            <span className="font-semibold">
              {isGetter ? 'Érték:' : 'Beállítva:'}
            </span>
            <span className="font-mono">
              {getValuePreview(data.executionData.value)}
            </span>
          </div>
          {data.executionData.timestamp && (
            <div className="text-xs opacity-75 mt-1">
              {new Date(data.executionData.timestamp).toLocaleTimeString()}
            </div>
          )}
        </div>
      )}

      {/* Scope indicator */}
      {data.config?.scope && (
        <div className="mt-2 text-xs text-emerald-200 bg-emerald-600 bg-opacity-30 px-2 py-1 rounded">
          <strong>Hatókör:</strong> {data.config.scope}
        </div>
      )}

      {/* Input handle (only for setter) */}
      {!isGetter && (
        <Handle
          type="target"
          position={Position.Left}
          className="w-3 h-3 bg-emerald-700 border-2 border-white"
        />
      )}

      {/* Output handle */}
      <Handle
        type="source"
        position={Position.Right}
        className="w-3 h-3 bg-emerald-700 border-2 border-white"
      />
    </div>
  );
};

// Wrapper komponensek a különböző típusokhoz
export const VariableSetNode: React.FC<NodeProps<FlowNode['data']>> = (props) => (
  <VariableNode {...props} nodeType="variable_set" />
);

export const VariableGetNode: React.FC<NodeProps<FlowNode['data']>> = (props) => (
  <VariableNode {...props} nodeType="variable_get" />
);

export default VariableNode;
